//  ----------      START   IMPORT  BLOCK       ----------
import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Container, Card, Form, Button, Row, Col, Spinner, Alert, ButtonGroup, ToggleButton } from "react-bootstrap";
// Components & Utils
import BaseLayout from "../BaseLayout/BaseLayout";
import SocialFooter from "../SocialFooter/SocialFooter";
// Context & Hooks
import { useLanguage } from "../../../../../API/Context/LanguageContext";
import { useRegister } from "../../../../../API/Hooks/useRegister";
// Config
import { 
  FaStethoscope, FaUserPlus, FaVenus, FaMars, FaCheckCircle, FaTimesCircle 
} from "react-icons/fa";
import { FRONTEND_ROUTES } from "../../../../../ConfigFiles/Routes/frontend_routes";
import { VocabRegister } from "../../../../../ConfigFiles/multilingual/VocabRegister";
import { VocabButtons } from "../../../../../ConfigFiles/multilingual/VocabButtons";
import "./RegisterForm.css";
//  ----------      END   IMPORT  BLOCK       ----------

const RegisterForm = () => {
  const { lan } = useLanguage();
  const navigate = useNavigate();
  
  // Hook for API logic & username checking
  const { 
    register, 
    isLoading, 
    error, 
    isSuccess,
    checkUsername,
    isCheckingUsername,
    usernameStatus,
    setUsernameStatus
  } = useRegister();

  // Local State for Form Data
  const [formData, setFormData] = useState({
    username: "",
    email: "",
    password: "",
    name: "",
    lastname_1: "",
    lastname_2: "",
    sex: 0,
    professional_request: false,
  });

  // Debounced API call to check username availability via hook
  useEffect(() => {
    const trimmedUsername = formData.username.trim();

    if (!trimmedUsername) {
      setUsernameStatus(null);
      return;
    }

    if (trimmedUsername.length < 3) {
      setUsernameStatus('too_short');
      return;
    }

    // 500ms debounce to prevent firing requests on every keystroke
    const timer = setTimeout(() => {
      checkUsername(trimmedUsername);
    }, 500);

    return () => clearTimeout(timer);
  }, [formData.username, checkUsername, setUsernameStatus]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;

    let finalValue;
    if (type === 'checkbox') {
      finalValue = checked;
    } else if (name === 'sex') {
      finalValue = Number(value);
    } else {
      finalValue = value;
    }

    setFormData({ 
      ...formData, 
      [name]: finalValue 
    });
  };

  const handleSexSelect = (sexValue) => {
    setFormData({
      ...formData,
      sex: sexValue
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (usernameStatus === 'taken' || usernameStatus === 'too_short' || isCheckingUsername) {
      return;
    }
    try {
      await register(formData);
    } catch (err) {
      console.error("Registration flow interrupted:", err);
    }
  };

  return (
    <BaseLayout>
      <Container className="d-flex align-items-center justify-content-center py-5 register-container">
        {!isSuccess ? (
          <Card className="register-card shadow-lg border-0">
            <Card.Body className="p-4 p-md-5">
              <div className="text-center mb-4">
                <FaUserPlus className="register-icon mb-2" />
                <h2 className="fw-bold">{VocabRegister.register_title[lan]}</h2>
                <p className="text-muted small">{VocabRegister.greetings[lan]}</p>
              </div>

              {error && (
                <Alert variant="danger" className="py-2 small text-center rounded-3">
                  {error}
                </Alert>
              )}

              <Form onSubmit={handleSubmit}>

                {/* --- USERNAME FIELD (CONNECTED TO CHECK SERVICE) --- */}
                <Form.Group className="mb-3">
                  <Form.Label className="small fw-bold">{VocabRegister.username[lan]}</Form.Label>
                  <div className="position-relative d-flex align-items-center">
                    <Form.Control
                      type="text"
                      name="username"
                      placeholder="johndoe"
                      value={formData.username}
                      onChange={handleChange}
                      required
                      className={`rounded-3 shadow-sm border-light pe-5 ${
                        usernameStatus === 'available' ? 'is-valid' : usernameStatus === 'taken' ? 'is-invalid' : ''
                      }`}
                    />
                    
                    {/* Inline Indicator / Loader */}
                    <div className="position-absolute end-0 me-3 d-flex align-items-center pointer-events-none">
                      {isCheckingUsername && <Spinner animation="border" size="sm" variant="secondary" />}
                      {!isCheckingUsername && usernameStatus === 'available' && (
                        <FaCheckCircle className="text-success" size={16} />
                      )}
                      {!isCheckingUsername && usernameStatus === 'taken' && (
                        <FaTimesCircle className="text-danger" size={16} />
                      )}
                    </div>
                  </div>

                  {/* Feedback Messages */}
                  {usernameStatus === 'too_short' && (
                    <Form.Text className="text-warning small ms-1">
                      Username must be at least 3 characters.
                    </Form.Text>
                  )}
                  {usernameStatus === 'available' && (
                    <Form.Text className="text-success small ms-1">
                      Username is available!
                    </Form.Text>
                  )}
                  {usernameStatus === 'taken' && (
                    <Form.Text className="text-danger small ms-1">
                      Username is already taken.
                    </Form.Text>
                  )}
                </Form.Group>

                {/* --- EMAIL FIELD --- */}
                <Form.Group className="mb-3">
                  <Form.Label className="small fw-bold">{VocabRegister.email[lan]}</Form.Label>
                  <Form.Control
                    type="email"
                    name="email"
                    placeholder="name@example.com"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    className="rounded-3 shadow-sm border-light"
                  />
                </Form.Group>

                {/* --- NAME & LASTNAMES --- */}
                <Row>
                  <Col md={12}>
                    <Form.Group className="mb-3">
                      <Form.Label className="small fw-bold">{VocabRegister.name[lan]}</Form.Label>
                      <Form.Control
                        type="text"
                        name="name"
                        value={formData.name}
                        onChange={handleChange}
                        required
                        className="rounded-3 shadow-sm border-light"
                      />
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label className="small fw-bold">{VocabRegister.lastname_1[lan]}</Form.Label>
                      <Form.Control
                        type="text"
                        name="lastname_1"
                        value={formData.lastname_1}
                        onChange={handleChange}
                        required
                        className="rounded-3 shadow-sm border-light"
                      />
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label className="small fw-bold">{VocabRegister.lastname_2[lan]}</Form.Label>
                      <Form.Control
                        type="text"
                        name="lastname_2"
                        value={formData.lastname_2}
                        onChange={handleChange}
                        className="rounded-3 shadow-sm border-light"
                      />
                    </Form.Group>
                  </Col>
                </Row>

                {/* --- PASSWORD FIELD --- */}
                <Form.Group className="mb-3">
                  <Form.Label className="small fw-bold">{VocabRegister.password[lan]}</Form.Label>
                  <Form.Control
                    type="password"
                    name="password"
                    value={formData.password}
                    onChange={handleChange}
                    required
                    className="rounded-3 shadow-sm border-light"
                  />
                </Form.Group>

                {/* --- CENTERED SEX SELECTOR BELOW PASSWORD --- */}
                <Form.Group className="mb-4 text-center">
                  <Form.Label className="small fw-bold text-muted d-block mb-2">
                    {VocabRegister.sex_at_birth[lan]}
                  </Form.Label>
                  <div className="d-flex justify-content-center">
                    <ButtonGroup className="sex-segmented-control shadow-sm">
                      <ToggleButton
                        id="sex-female"
                        type="radio"
                        variant={formData.sex === 0 ? "primary" : "outline-secondary"}
                        name="sex"
                        value={0}
                        checked={formData.sex === 0}
                        onChange={() => handleSexSelect(0)}
                        className="py-2 px-4 text-capitalize d-flex align-items-center justify-content-center gap-2 small fw-semibold"
                      >
                        <FaVenus size={14} />
                        {VocabRegister.female[lan]}
                      </ToggleButton>
                      <ToggleButton
                        id="sex-male"
                        type="radio"
                        variant={formData.sex === 1 ? "primary" : "outline-secondary"}
                        name="sex"
                        value={1}
                        checked={formData.sex === 1}
                        onChange={() => handleSexSelect(1)}
                        className="py-2 px-4 text-capitalize d-flex align-items-center justify-content-center gap-2 small fw-semibold"
                      >
                        <FaMars size={14} />
                        {VocabRegister.male[lan]}
                      </ToggleButton>
                    </ButtonGroup>
                  </div>
                </Form.Group>

                {/* --- PROFESSIONAL REQUEST TOGGLE --- */}
                <div className="mb-4 p-3 rounded-3 bg-light border d-flex align-items-center justify-content-between shadow-sm">
                  <div className="d-flex align-items-center">
                    <FaStethoscope className="text-primary me-3" size={20} />
                    <div>
                      <p className="mb-0 small fw-bold">{VocabRegister.professional_toggle_title[lan]}</p>
                      <p className="mb-0 xx-small text-muted">{VocabRegister.professional_toggle_desc[lan]}</p>
                    </div>
                  </div>
                  <Form.Check 
                    type="switch"
                    id="professional-request-switch"
                    name="professional_request"
                    checked={formData.professional_request}
                    onChange={handleChange}
                    className="custom-switch"
                  />
                </div>

                {/* --- SUBMIT BUTTON --- */}
                <Button 
                  type="submit" 
                  variant="primary" 
                  className="w-100 py-2 fw-bold rounded-pill shadow-sm"
                  disabled={isLoading || usernameStatus === 'taken' || usernameStatus === 'too_short' || isCheckingUsername}
                >
                  {isLoading ? <Spinner size="sm" /> : VocabRegister.register_label[lan]}
                </Button>
              </Form>

              <div className="text-center mt-4">
                <span className="text-muted small">
                  {VocabRegister.already_have_account[lan]} 
                  <Button variant="link" className="p-0 ms-1 small fw-bold text-decoration-none" onClick={() => navigate(FRONTEND_ROUTES.auth)}>
                    {VocabRegister.login[lan]}
                  </Button>
                </span>
              </div>
            </Card.Body>
          </Card>
        ) : (
          /* SUCCESS SLIDE */
          <Card className="register-card success-card shadow-lg border-0 text-center scale-in-center">
            <Card.Body className="p-5">
              <div className="success-check-wrapper mb-4">
                <div className="success-check-circle"></div>
              </div>
              <h3 className="fw-bold">{VocabRegister.post_submit_title[lan]}</h3>
              <p className="text-muted mb-4">{VocabRegister.post_submit_message[lan]}</p>
              
              <Button 
                variant="outline-primary" 
                className="rounded-pill px-5 mb-5 fw-bold"
                onClick={() => navigate(FRONTEND_ROUTES.landing)}
              >
                {VocabRegister.back_home[lan]}
              </Button>

              <SocialFooter/>
            </Card.Body>
          </Card>
        )}
      </Container>
    </BaseLayout>
  );
};

export default RegisterForm;